import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  DEFAULT_LOCALE,
  isRtlLocale,
  normalizeLocale,
  type SupportedLocale,
} from '../../config/localeConfig';

type LocaleContextValue = {
  locale: SupportedLocale;
  direction: 'ltr' | 'rtl';
};

const LocaleContext = createContext<LocaleContextValue | null>(null);

export function LocaleProvider({
  children,
  locale,
  direction,
}: {
  children: React.ReactNode;
  locale: SupportedLocale;
  direction: 'ltr' | 'rtl';
}) {
  const value = useMemo(() => ({ locale, direction }), [locale, direction]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocaleContext() {
  const params = useParams();
  const ctx = useContext(LocaleContext);

  if (ctx) return ctx;

  const routeLocale = normalizeLocale(params.locale || params.lang || DEFAULT_LOCALE);
  return {
    locale: routeLocale,
    direction: isRtlLocale(routeLocale) ? 'rtl' : 'ltr',
  };
}
